import React, { useEffect, useState } from "react";
import { Download, RotateCcw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api, { errMsg } from "../api";
import { useToast } from "../hooks/use-toast";

function Card({ children, className = "", ...rest }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`} {...rest}>{children}</div>;
}

const STAGES = ["Not Registered", "Registered", "Training Scheduled", "Training Started", "Training Completed", "Training Completion Confirmed", "Ready for Production", "Active", "Not Currently Active"];
const PATRIOT = ["Not Started", "Ready for Patriot", "Contractor Created", "Invitation Sent", "Contractor Registered", "Banking Setup Complete", "Active", "Error", "Requires Administrator Action"];

function PatriotRow({ p, onSaved }) {
  const { toast } = useToast();
  const [f, setF] = useState({ patriot_status: p.patriot_status, patriot_contractor_id: p.patriot_contractor_id || "", patriot_last_error: p.patriot_last_error || "", notes: p.notes || "" });
  const [busy, setBusy] = useState(false);
  const dirty = f.patriot_status !== p.patriot_status || f.patriot_contractor_id !== (p.patriot_contractor_id || "") || f.patriot_last_error !== (p.patriot_last_error || "") || f.notes !== (p.notes || "");
  const isErr = ["Error", "Requires Administrator Action"].includes(f.patriot_status);

  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/patriot/${p.contractor_id}`, f); toast({ title: "Patriot record saved" }); onSaved(); }
    catch (e) { toast({ title: "Save failed", description: errMsg(e), variant: "destructive" }); }
    finally { setBusy(false); }
  };
  const retry = async () => {
    setBusy(true);
    try { const r = await api.post(`/admin/patriot/${p.contractor_id}/retry`); toast({ title: "Retry recorded", description: r.data.attempt.result }); onSaved(); }
    catch (e) { toast({ title: "Retry failed", description: errMsg(e), variant: "destructive" }); }
    finally { setBusy(false); }
  };

  return (
    <tr className="border-b border-slate-50 align-top" data-testid={`patriot-row-${p.contractor_id}`}>
      <td className="py-3 pr-4"><div className="font-medium text-slate-800">{p.name}</div><div className="text-xs text-slate-400">{p.email}</div></td>
      <td className="py-3 pr-4">
        <select className="w-48 rounded-lg border border-slate-200 px-2 py-1.5 text-sm" value={f.patriot_status} onChange={(e) => setF({ ...f, patriot_status: e.target.value })} data-testid={`patriot-select-${p.contractor_id}`}>
          {PATRIOT.map((s) => <option key={s}>{s}</option>)}
        </select>
        {isErr && <Input className="mt-2" placeholder="Error details" value={f.patriot_last_error} onChange={(e) => setF({ ...f, patriot_last_error: e.target.value })} data-testid={`patriot-error-${p.contractor_id}`} />}
        {p.patriot_last_error && <div className="mt-1 text-xs text-rose-600">Last error: {p.patriot_last_error}</div>}
      </td>
      <td className="py-3 pr-4"><Input className="w-40" placeholder="Patriot ID" value={f.patriot_contractor_id} onChange={(e) => setF({ ...f, patriot_contractor_id: e.target.value })} data-testid={`patriot-id-${p.contractor_id}`} /></td>
      <td className="py-3 pr-4"><Input className="w-44" placeholder="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid={`patriot-notes-${p.contractor_id}`} /></td>
      <td className="py-3 pr-4 text-xs text-slate-500">
        <div>{p.patriot_sync_status}</div>
        <div>Attempts: {p.sync_attempts || 0}</div>
        {p.updated_at && <div>{new Date(p.updated_at).toLocaleDateString()}</div>}
      </td>
      <td className="py-3">
        <div className="flex gap-2">
          <Button size="sm" disabled={!dirty || busy} onClick={save} data-testid={`patriot-save-${p.contractor_id}`}>Save</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={retry} className="gap-1" data-testid={`patriot-retry-${p.contractor_id}`}><RotateCcw className="h-3.5 w-3.5" /> Retry</Button>
        </div>
      </td>
    </tr>
  );
}

export default function PatriotPanel() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get("/admin/patriot").then((r) => setData(r.data)).catch((e) => toast({ title: "Could not load", description: errMsg(e), variant: "destructive" }));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/patriot/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "patriot_contractors_ready.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast({ title: "Export failed", description: errMsg(e), variant: "destructive" }); }
  };

  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  const people = data.people.filter((p) => !q || `${p.name} ${p.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="patriot-panel">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-bold text-slate-900">Patriot Software Setup</h2>
            <p className="max-w-2xl text-sm text-slate-500">Contractors enter their own direct-deposit banking in Patriot's secure Contractor Portal — no bank details are stored here. Mark people "Ready for Patriot", export the CSV for Patriot's bulk contractor import, then record the Patriot contractor ID.</p>
            <div className="mt-2 inline-flex rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700" data-testid="patriot-api-status">{data.message}</div>
          </div>
          <Button variant="outline" onClick={exportCsv} className="gap-1.5" data-testid="patriot-export-btn"><Download className="h-4 w-4" /> Export Ready CSV</Button>
        </div>
      </Card>
      <Card>
        <Input className="mb-3 max-w-xs" placeholder="Search contractors…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="patriot-search" />
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-400">
              <tr><th className="py-2 pr-4">Contractor</th><th className="py-2 pr-4">Patriot status</th><th className="py-2 pr-4">Patriot ID</th><th className="py-2 pr-4">Notes</th><th className="py-2 pr-4">Sync</th><th className="py-2">Actions</th></tr>
            </thead>
            <tbody>
              {people.map((p) => <PatriotRow key={`${p.contractor_id}-${p.updated_at}`} p={p} onSaved={load} />)}
              {people.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">No contractors match.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export { STAGES };
